import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocalStorage } from 'usehooks-ts';

import { Button, Stack } from '@mui/material';

import ControlWithLabel from '../ControlWithLabel';
import TeamCard from '../TeamCard';
import ModalBody from './ModalBody';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import { ModalContentProps } from './ModalManager';
import { Team, useGetPersonDetailsQuery } from 'src/store/sageHomeApi';
import { selectBearerToken } from 'src/store/slices/authSlice';
import {
    SELECTED_TEAM_KEY,
    selectCurrentTeamRid,
    setSelectedTeamRid,
} from 'src/store/slices/teamMemberSlice';
import { useAppDispatch } from 'src/store/store';

function SelectTeamModal({ onClose }: ModalContentProps) {
    const dispatch = useAppDispatch();

    const bearerToken = useSelector(selectBearerToken);
    const currentTeamRid = useSelector(selectCurrentTeamRid);

    const [_storedTeamRid, setCachedTeamRid] = useLocalStorage(SELECTED_TEAM_KEY, '');

    // Get teams directly from RTK Query cache
    const { data: personDetails } = useGetPersonDetailsQuery(
        { authorization: bearerToken },
        { skip: !bearerToken }
    );
    const allTeams = personDetails?.teams ?? [];

    const currentTeam = allTeams.find((team) => team.teamRid === currentTeamRid);

    const [selectedTeam, setSelectedTeam] = useState<Team | undefined>(currentTeam);

    // Sync selectedTeam when currentTeam becomes available
    useEffect(() => {
        if (currentTeam && !selectedTeam) {
            setSelectedTeam(currentTeam);
        }
    }, [currentTeam, selectedTeam]);

    const handleSelectTeam = () => {
        if (selectedTeam && selectedTeam.teamRid !== currentTeam?.teamRid) {
            setCachedTeamRid(selectedTeam.teamRid);
            dispatch(setSelectedTeamRid(selectedTeam.teamRid));
        }

        onClose();
    };

    const isCurrentTeam = selectedTeam && selectedTeam?.teamRid === currentTeam?.teamRid;

    return (
        <>
            <ModalHeader title="Select Your Team" onClose={onClose} />
            <ModalBody>
                <Stack sx={{ gap: 2, width: '100%' }}>
                    {allTeams.map((team) => (
                        <ControlWithLabel
                            key={team.teamRid}
                            label={<TeamCard team={team} />}
                            checked={team.teamRid === selectedTeam?.teamRid}
                            onChange={() => setSelectedTeam(team)}
                            controlType="radio"
                        />
                    ))}
                </Stack>
            </ModalBody>

            <ModalFooter>
                <Button
                    variant="contained"
                    color={isCurrentTeam ? 'primaryInverse' : 'primary'}
                    fullWidth
                    onClick={handleSelectTeam}
                >
                    {isCurrentTeam ? 'Cancel' : 'Change Team'}
                </Button>
            </ModalFooter>
        </>
    );
}

export default SelectTeamModal;
